import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Clock, TrendingUp, AlertTriangle, RefreshCw, CreditCard } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { PurchaseCreditsModal } from '../billing/PurchaseCreditsModal';
import { AutoTopupSettings } from './AutoTopupSettings';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { formatCents } from '../../utils/credits';

interface CreditBalance {
  available_balance: string | number;
  total_granted: string | number;
  total_used: string | number;
  low_balance_threshold: string | number;
  margin_percentage: string | number;
  active_grants_count: number;
}

interface CreditGrant {
  id: string;
  grant_type: string;
  amount_usd: string | number;
  amount_used: string | number;
  amount_remaining: string | number;
  description: string;
  status: string;
  expires_at: string | null;
  created_at: string;
  reference_id: string | null;
  granted_by: string | null;
}

const CreditsSection = () => {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [grants, setGrants] = useState<CreditGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'balance' | 'autotopup'>('balance');
  const api = useAuthenticatedApi();
  const { hasActiveSubscription, loading: subscriptionLoading } = useSubscriptionStatus();
  const { accessType, loading: accessLoading } = useSubscriptionAccess();

  useEffect(() => {
    // Small delay to ensure authentication is ready
    const timer = setTimeout(() => {
      if (api) {
        fetchCreditData();
      }
    }, 200);
    
    return () => clearTimeout(timer);
  }, []);

  const fetchCreditData = async () => {
    if (!api) {
      setError('API not available');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      // Fetch balance and grants in parallel
      const [balanceResponse, grantsResponse] = await Promise.all([
        api.get(apiEndpoints.billing.balance),
        api.get(`${apiEndpoints.billing.grants}?include_expired=true`)
      ]);

      // Set the data from API responses
      if (balanceResponse) {
        setBalance(balanceResponse);
      }
      
      if (grantsResponse) {
        // Ensure grants is an array
        const grantsData = Array.isArray(grantsResponse) 
          ? grantsResponse 
          : [];
        setGrants(grantsData);
      } else {
        setGrants([]);
      }
    } catch (err: any) {
      console.error('Error fetching credit data:', err);
      // Provide more specific error messages based on the error
      if (err.response && err.response.status === 404) {
        setError('Credit system is not yet available');
      } else {
        setError('Failed to load credit information');
      }
      // Set default empty data on error
      setGrants([]);
    } finally {
      setLoading(false);
    }
  };

  // Credits are displayed in cents
  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getGrantTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      signup_bonus: 'Signup Bonus',
      promotional: 'Promotional',
      referral_bonus: 'Referral Bonus',
      purchased: 'Purchased',
      support_credit: 'Support Credit',
      milestone_reward: 'Milestone Reward',
      compensation: 'Compensation',
      trial: 'Trial',
      subscription_included: 'Subscription Included',
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status: string) => {
    const styles: { [key: string]: string } = {
      active: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      depleted: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400',
      expired: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      cancelled: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || ''}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handlePurchaseSuccess = () => {
    setShowPurchaseModal(false);
    fetchCreditData(); // Refresh balance after purchase
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-slate-200 dark:bg-neutral-700 rounded w-1/4 mb-4"></div>
          <div className="h-8 bg-slate-200 dark:bg-neutral-700 rounded w-1/2 mb-8"></div>
          <div className="space-y-4">
            <div className="h-20 bg-slate-200 dark:bg-neutral-700 rounded"></div>
            <div className="h-20 bg-slate-200 dark:bg-neutral-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const isLowBalance = balance && 
    parseFloat(balance.available_balance.toString()) < parseFloat(balance.low_balance_threshold.toString());

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white">Credits</h2>
        <div className="flex items-center gap-2">
          <div className="relative group">
            {(() => {
              const canPurchase = hasActiveSubscription || accessType === 'free_access';
              const isPurchaseLoading = subscriptionLoading || accessLoading;
              return (
                <>
                  <button
                    onClick={() => setShowPurchaseModal(true)}
                    disabled={!canPurchase || isPurchaseLoading}
                    aria-disabled={!canPurchase || isPurchaseLoading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors shadow-sm ${
                      !canPurchase || isPurchaseLoading
                        ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 cursor-not-allowed'
                        : 'bg-brand-primary hover:bg-brand-primaryDark text-black'
                    }`}
                  >
                    <CreditCard size={16} />
                    Purchase Credits
                  </button>
                  {!canPurchase && !isPurchaseLoading && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block pointer-events-none">
                      <div className="px-3 py-2 text-xs text-white bg-black/80 rounded-md whitespace-nowrap shadow-lg">
                        You need an active subscription to purchase credits
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          <button
            onClick={fetchCreditData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-4 mb-6 border-b border-slate-300 dark:border-neutral-700">
        <button
          onClick={() => setActiveTab('balance')}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'balance'
              ? 'text-neutral-900 dark:text-white border-b-2 border-neutral-900 dark:border-white'
              : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white'
          }`}
        >
          Balance & History
        </button>
        <button
          onClick={() => setActiveTab('autotopup')}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'autotopup'
              ? 'text-neutral-900 dark:text-white border-b-2 border-neutral-900 dark:border-white'
              : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white'
          }`}
        >
          Auto Top-up
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'balance' && (
        <>
          {/* Balance Overview */}
          <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Available Balance */}
          <div className="bg-slate-100 dark:bg-neutral-800 rounded-lg p-6 border border-slate-300 dark:border-neutral-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-neutral-600 dark:text-neutral-400">Available Balance</span>
            </div>
            <div className={`text-2xl font-bold ${isLowBalance ? 'text-red-600 dark:text-red-400' : 'text-neutral-900 dark:text-white'}`}>
              {balance ? formatCents(balance.available_balance) : '0'}
            </div>
            {isLowBalance && (
              <div className="flex items-center gap-1 mt-2 text-xs text-red-600 dark:text-red-400">
                <AlertTriangle size={14} />
                <span>Low balance</span>
              </div>
            )}
          </div>

          {/* Total Used */}
          <div className="bg-slate-100 dark:bg-neutral-800 rounded-lg p-6 border border-slate-300 dark:border-neutral-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-neutral-600 dark:text-neutral-400">Total Used</span>
              <TrendingUp size={20} className="text-neutral-400" />
            </div>
            <div className="text-2xl font-bold text-neutral-900 dark:text-white">
              {balance ? formatCents(balance.total_used) : '0'}
            </div>
          </div>

          {/* Active Grants */}
          <div className="bg-slate-100 dark:bg-neutral-800 rounded-lg p-6 border border-slate-300 dark:border-neutral-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-neutral-600 dark:text-neutral-400">Active Grants</span>
              <Clock size={20} className="text-neutral-400" />
            </div>
            <div className="text-2xl font-bold text-neutral-900 dark:text-white">
              {balance ? balance.active_grants_count : 0}
            </div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Total granted: {balance ? formatCents(balance.total_granted) : '0'}
            </div>
          </div>
        </div>

        {/* Low Balance Alert Settings */}
        <div className="bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-800/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-yellow-700 dark:text-yellow-400 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Low balance threshold: <strong>{balance ? formatCents(balance.low_balance_threshold) : '500'}</strong>
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                You'll receive alerts when your balance falls below this amount.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Credit Grant History */}
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Credit History</h3>
        
        {!grants || grants.length === 0 ? (
          <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
            <p>No credit grants found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-300 dark:border-neutral-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-600 dark:text-neutral-400">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-600 dark:text-neutral-400">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-600 dark:text-neutral-400">Description</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-neutral-600 dark:text-neutral-400">Amount</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-neutral-600 dark:text-neutral-400">Used</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-neutral-600 dark:text-neutral-400">Remaining</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-neutral-600 dark:text-neutral-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((grant) => (
                  <tr key={grant.id} className="border-b border-slate-200 dark:border-neutral-800 hover:bg-slate-50 dark:hover:bg-neutral-900">
                    <td className="py-3 px-4 text-sm text-neutral-900 dark:text-white">
                      {formatDate(grant.created_at)}
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-900 dark:text-white">
                      {getGrantTypeLabel(grant.grant_type)}
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-600 dark:text-neutral-400">
                      {grant.description}
                      {grant.expires_at && (
                        <span className="block text-xs text-neutral-500 dark:text-neutral-500 mt-1">
                          Expires: {formatDate(grant.expires_at)}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-neutral-900 dark:text-white">
                      {formatCents(grant.amount_usd)}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-neutral-600 dark:text-neutral-400">
                      {formatCents(grant.amount_used)}
                    </td>
                    <td className="py-3 px-4 text-sm text-right font-medium text-neutral-900 dark:text-white">
                      {formatCents(grant.amount_remaining)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {getStatusBadge(grant.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}

      {activeTab === 'autotopup' && (
        <AutoTopupSettings />
      )}

      {/* Purchase Credits Modal */}
      <PurchaseCreditsModal
        show={showPurchaseModal}
        onHide={() => setShowPurchaseModal(false)}
        onSuccess={handlePurchaseSuccess}
      />
    </div>
  );
};

export default CreditsSection; 